import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Card, Divider } from 'ui-components';
import {
  RETIREMENT_CALCULATOR_NAMESPACE,
  RetirementCalculatorField,
  RetirementCalculatorStep,
} from '../../tools/RetirementCalculator.const';
import { RetirementCalculatorSummaryPropsModel } from '../../tools/RetirementCalculator.model';
import { RetirementCalculatorSummarySection } from './RetirementCalculatorSummarySection';
import { RetirementCalculatorSummarySettingsSection } from './RetirementCalculatorSummarySettingsSection';
import {
  createBasicRows,
  createSavingsRows,
  createSettingsRows,
} from './RetirementCalculatorSummary.utils';

import useStyles from './RetirementCalculatorSummary.style';

export const RetirementCalculatorSummary: React.FC<RetirementCalculatorSummaryPropsModel> = ({
  basicData,
  savings,
  settings,
  settingsOverrides,
  onSettingsOverridesChange,
  onEdit,
  className,
}) => {
  const c = useStyles({});
  const [t] = useTranslation(RETIREMENT_CALCULATOR_NAMESPACE, { useSuspense: false });

  const gender = basicData?.[RetirementCalculatorField.GENDER];
  const basicRows = useMemo(() => createBasicRows(basicData, t), [basicData, t]);
  const savingsRows = useMemo(() => createSavingsRows(savings, t), [savings, t]);
  const settingsRows = useMemo(
    () => createSettingsRows({
      settings,
      settingsOverrides,
      gender,
      onSettingsOverridesChange,
      t,
    }),
    [gender, onSettingsOverridesChange, settings, settingsOverrides, t],
  );

  return (
    <Card className={clsx(c.root, className)} type="ghost" testId="RetirementCalculatorSummary">
      <RetirementCalculatorSummarySection
        title={t('summary.basic-data.title', { step: RetirementCalculatorStep.BASIC_DATA })}
        rows={basicRows}
        step={RetirementCalculatorStep.BASIC_DATA}
        onEdit={onEdit}
      />

      <Divider className={c.divider} />
      <RetirementCalculatorSummarySection
        title={t('summary.savings.title', { step: RetirementCalculatorStep.SAVINGS })}
        rows={savingsRows}
        step={RetirementCalculatorStep.SAVINGS}
        emptyState={t('summary.savings.empty')}
        onEdit={onEdit}
      />

      <Divider className={c.divider} />
      <RetirementCalculatorSummarySettingsSection
        title={t('summary.advanced.title')}
        rows={settingsRows}
      />
    </Card>
  );
};
